import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { GraduationCap, MessageCircle, Languages, Award, ShieldCheck, Users } from "lucide-react";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { resolveImageUrl } from "../lib/imageUrl";
import GuideRequestForm from "../components/GuideRequestForm";

const GuideCard = ({ g, waHref }) => (
    <motion.div
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.4 }}
        className="bg-emerald_surface border border-border_green flex flex-col"
        data-testid={`guide-card-${g.id}`}
    >
        <div className="aspect-[4/5] bg-emerald_deep relative overflow-hidden">
            {g.photo ? (
                <img src={resolveImageUrl(g.photo)} alt={g.name} className="w-full h-full object-cover" loading="lazy" />
            ) : (
                <div className="w-full h-full flex items-center justify-center text-muted_green">
                    <GraduationCap className="w-20 h-20" />
                </div>
            )}
            {g.experience_years > 0 && (
                <div className="absolute top-3 right-3 bg-emerald_deep/85 backdrop-blur px-2 py-1 text-[10px] uppercase tracking-[0.15em] text-gold border border-gold/40">
                    <Award className="w-3 h-3 inline -mt-0.5 mr-1" /> {g.experience_years} yıl
                </div>
            )}
        </div>
        <div className="p-5 flex-1 flex flex-col">
            <h3 className="font-serif text-xl text-bone leading-tight">{g.name}</h3>
            {g.title && <div className="text-xs text-gold uppercase tracking-[0.18em] mt-1">{g.title}</div>}
            {g.bio && <p className="text-sm text-bone/80 mt-3 leading-relaxed line-clamp-5">{g.bio}</p>}
            {(g.languages || []).length > 0 && (
                <div className="flex items-center gap-1.5 mt-3 text-[11px] text-muted_green">
                    <Languages className="w-3.5 h-3.5 text-gold" />
                    <span>{(g.languages || []).join(" · ")}</span>
                </div>
            )}
            <a href={waHref} target="_blank" rel="noreferrer" className="btn-outline-gold !text-xs mt-auto justify-center pt-5">
                <MessageCircle className="w-3.5 h-3.5" /> İletişime Geç
            </a>
        </div>
    </motion.div>
);

const Guides = () => {
    const { settings } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        api.get("/guides").then(({ data }) => { if (alive) setItems(data || []); }).finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, []);

    const wa = (settings.whatsapp_number || "905555555555").replace(/\D/g, "");
    const waHref = `https://wa.me/${wa}?text=${encodeURIComponent("Merhaba, rehberlik hizmetiniz hakkında bilgi alabilir miyim?")}`;

    return (
        <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16"
            data-testid="guides-page"
        >
            <div className="text-center mb-12 max-w-3xl mx-auto">
                <div className="text-xs uppercase tracking-[0.35em] text-gold mb-3">REHBERLİK HİZMETİMİZ</div>
                <h1 className="font-serif text-4xl sm:text-5xl text-bone tracking-tight">Profesyonel Hocalarımız</h1>
                <p className="text-bone/70 mt-4 leading-relaxed">
                    Umre ve Hac yolculuğunuzda gruplara rehberlik yapacak alanında uzman, ilim ehli hocalarımız bulunmaktadır.
                    Kutsal topraklarda mübarek ibadetlerinizin usul ve adabına uygun şekilde yerine getirilmesi için yanınızdayız.
                </p>
                <div className="flex items-center justify-center gap-6 mt-6 flex-wrap">
                    <div className="flex items-center gap-2 text-xs text-muted_green">
                        <ShieldCheck className="w-4 h-4 text-gold" /> Tecrübeli, ehil hocalar
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted_green">
                        <Users className="w-4 h-4 text-gold" /> Grup organizasyonları
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted_green">
                        <Languages className="w-4 h-4 text-gold" /> Türkçe · Arapça
                    </div>
                </div>
            </div>

            {loading ? (
                <div className="text-center text-muted_green py-12">Yükleniyor…</div>
            ) : items.length === 0 ? (
                <div className="text-center py-16 border border-dashed border-border_green" data-testid="guides-empty">
                    <GraduationCap className="w-14 h-14 text-muted_green mx-auto mb-4" />
                    <h3 className="font-serif text-2xl text-bone mb-2">Profesyonel Hoca Kadromuz</h3>
                    <p className="text-muted_green text-sm max-w-md mx-auto leading-relaxed">
                        Gruplara rehberlik yapacak, alanında uzman, ehil hocalarımız bulunmaktadır. Detaylı bilgi ve grup organizasyonu için bizimle iletişime geçin.
                    </p>
                    <a href={waHref} target="_blank" rel="noreferrer" className="btn-gold mt-6 inline-flex" data-testid="guides-empty-wa">
                        <MessageCircle className="w-4 h-4" /> WhatsApp ile İletişim
                    </a>
                </div>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="guides-grid">
                    {items.map((g) => <GuideCard key={g.id} g={g} waHref={waHref} />)}
                </div>
            )}

            <div className="mt-16 lg:mt-20">
                <div className="text-center mb-8 max-w-2xl mx-auto">
                    <div className="text-xs uppercase tracking-[0.35em] text-gold mb-3">İLETİŞİM & TALEP</div>
                    <h2 className="font-serif text-3xl sm:text-4xl text-bone tracking-tight">Talep Formu</h2>
                    <p className="text-bone/70 mt-3 text-sm">Grubunuz için özel rehberlik talebi oluşturun — ekibimiz sizinle doğrudan iletişime geçsin.</p>
                </div>
                <div className="max-w-4xl mx-auto">
                    <GuideRequestForm />
                </div>
            </div>
        </motion.section>
    );
};

export default Guides;
